import { useState, useEffect, useRef, useCallback } from "react"
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  Handshake,
  Newspaper,
  Brain,
  CalendarDays,
  ArrowRight,
  BookOpen,
  Layers,
  Compass,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"

const navItems = [
  {
    label: "Our Companies",
    href: "/companies",
    children: [
      { label: "Connexr", href: "/companies/connexr", desc: "Enterprise AI & Managed Services", icon: Layers, badge: "Flagship" },
      { label: "LeoRix AI", href: "/leorix-ai", desc: "Enterprise AI Brain", icon: Brain },
    ],
  },
  {
    label: "About RSA Tech Group",
    href: "/about",
    children: [
      { label: "About Us", href: "/about", desc: "Our story, mission, and values", icon: Building2 },
      { label: "Leadership Team", href: "/leadership-team", desc: "Meet the people driving RSA Tech Group", icon: Users },
      { label: "Partners", href: "/partners", desc: "Strategic technology alliances", icon: Handshake },
      { label: "Methodology", href: "/methodology", desc: "How we deliver results", icon: Compass },
    ],
  },
  {
    label: "Careers",
    href: "/careers",
  },
  {
    label: "Insights",
    href: "/blogs-insights",
    children: [
      { label: "Blogs & Articles", href: "/blogs-insights", desc: "Latest thoughts and deep dives", icon: BookOpen },
      { label: "News & Press", href: "/news-insights", desc: "Latest news and press releases", icon: Newspaper },
      { label: "Events", href: "/events", desc: "Upcoming conferences & meetups", icon: CalendarDays },
    ],
  },
]

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openMobileSection, setOpenMobileSection] = useState<string | null>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [navTheme, setNavTheme] = useState<"light" | "dark">("light")
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const location = useLocation()

  // Detect which section the navbar is over
  const updateNavTheme = useCallback(() => {
    const sections = document.querySelectorAll<HTMLElement>("[data-nav-theme]")
    if (sections.length === 0) {
      setNavTheme("light")
      return
    }
    // Check which section the navbar bottom edge is inside
    // Navbar height: h-14 (56px) / sm:h-16 (64px) / lg:h-20 (80px)
    const checkPoint = 80
    let theme: "light" | "dark" = "light"
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect()
      if (rect.top <= checkPoint && rect.bottom > checkPoint) {
        theme = (section.dataset.navTheme as "light" | "dark") || "light"
      }
    })
    setNavTheme(theme)
  }, [])

  useEffect(() => {
    updateNavTheme()
    window.addEventListener("scroll", updateNavTheme, { passive: true })
    return () => window.removeEventListener("scroll", updateNavTheme)
  }, [updateNavTheme, location.pathname])

  useEffect(() => {
    window.scrollTo(0, 0)
    setActiveDropdown(null)
    setIsMobileMenuOpen(false)
    setOpenMobileSection(null)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isMobileMenuOpen])

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current)
    setActiveDropdown(label)
  }

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 150)
  }

  const getGridCols = (count: number) => {
    if (count <= 3) return "sm:grid-cols-1 md:grid-cols-3"
    if (count <= 4) return "sm:grid-cols-2 md:grid-cols-2"
    if (count <= 6) return "sm:grid-cols-2 md:grid-cols-3"
    return "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
  }

  const isDark = navTheme === "dark" && !activeDropdown

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <style>{`
        @keyframes megaSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .mega-enter { animation: megaSlideDown 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes mobileSlideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .mobile-item-enter { animation: mobileSlideIn 0.2s ease-out forwards; opacity: 0; }
      `}</style>

      {/* Main nav bar */}
      <div className={cn(
        "transition-all duration-500 ease-out",
        isDark
          ? "bg-transparent backdrop-blur-xl"
          : "bg-white/70 backdrop-blur-2xl",
      )}>
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/rsa-logo.png"
                alt="RSA Tech Group"
                className={cn(
                  "h-8 sm:h-10 md:h-12 lg:h-14 w-auto transition-all duration-500",
                  isDark && "brightness-0 invert",
                )}
                width={200}
                height={64}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-4">
              {navItems.map((item, idx) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children ? (
                    <>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-1 font-medium transition-all duration-300 py-4 px-2 xl:px-3 text-[13px] xl:text-[15px] whitespace-nowrap",
                          activeDropdown === item.label
                            ? "text-[#FDA400]"
                            : location.pathname.startsWith(item.href)
                              ? "text-[#FDA400] font-bold"
                              : isDark
                                ? "text-white/80 hover:text-[#FDA400]"
                                : "text-[#1C2B6E]/80 hover:text-[#FDA400]",
                        )}
                      >
                        {item.label}
                        <ChevronDown className={cn(
                          "h-3.5 w-3.5 transition-transform duration-200",
                          activeDropdown === item.label && "rotate-180",
                        )} />
                      </Link>

                      {/* Dropdown card */}
                      {activeDropdown === item.label && (
                        <div className={cn(
                          "mega-enter absolute top-full pt-3 z-50",
                          idx >= 1 ? "right-0" : "left-1/2 -translate-x-1/2",
                          item.children.length > 6
                            ? "w-[780px]"
                            : item.children.length > 4
                              ? "w-[680px]"
                              : item.children.length > 3
                                ? "w-[560px]"
                                : "w-[480px]",
                        )}>
                          <div className="bg-white rounded-2xl shadow-[0_25px_80px_rgba(28,43,110,0.15)] border border-gray-100/80 overflow-hidden">
                            {/* Header */}
                            <div className="px-6 pt-5 pb-3 flex items-center gap-3">
                              <div className="h-px w-6 bg-[#FDA400]" />
                              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FDA400]">
                                {item.label}
                              </span>
                              <div className="h-px flex-1 bg-gray-100" />
                              <Link
                                to={item.href}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1C2B6E]/50 hover:text-[#FDA400] transition-colors"
                              >
                                View all <ArrowRight className="w-3 h-3" />
                              </Link>
                            </div>

                            {/* Grid */}
                            <div className={cn(
                              "grid gap-1 px-3 pb-4",
                              item.children.length <= 3 ? "grid-cols-1" :
                              item.children.length <= 4 ? "grid-cols-2" :
                              "grid-cols-3",
                            )}>
                              {item.children.map((child) => (
                                <Link
                                  key={child.label}
                                  to={child.href}
                                  className={cn(
                                    "group/item flex items-start gap-3 p-3 rounded-xl transition-all duration-200 hover:bg-gradient-to-br hover:from-[#1C2B6E]/[0.03] hover:to-[#FDA400]/[0.03]",
                                    location.pathname === child.href && "bg-[#FDA400]/[0.06] ring-1 ring-[#FDA400]/20",
                                  )}
                                >
                                  <div className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200",
                                    location.pathname === child.href
                                      ? "bg-[#1C2B6E] text-[#FDA400]"
                                      : "bg-[#1C2B6E]/[0.06] text-[#1C2B6E]/50 group-hover/item:bg-[#1C2B6E] group-hover/item:text-[#FDA400]",
                                  )}>
                                    <child.icon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className={cn(
                                        "text-[13px] font-semibold transition-colors duration-200",
                                        location.pathname === child.href
                                          ? "text-[#1C2B6E]"
                                          : "text-gray-800 group-hover/item:text-[#1C2B6E]",
                                      )}>
                                        {child.label}
                                      </span>
                                      {"badge" in child && child.badge && (
                                        <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FDA400]/15 text-[#FDA400] border border-[#FDA400]/20">
                                          {child.badge}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-[11px] text-gray-400 leading-snug mt-0.5 line-clamp-1">
                                        {child.desc}
                                      </p>
                                      {"badge" in child && child.badge === "Flagship" && (
                                        <a
                                          href="https://connexr.com"
                                          target="_blank"
                                          rel="noopener"
                                          onClick={(e) => e.stopPropagation()}
                                          className="flex-shrink-0 text-[10px] font-semibold text-[#FDA400] hover:text-[#1C2B6E] transition-colors mt-0.5"
                                        >
                                          connexr.com ↗
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "font-medium transition-all duration-300 py-4 px-2 xl:px-3 text-[13px] xl:text-[15px] whitespace-nowrap block",
                        location.pathname === item.href
                          ? "text-[#FDA400] font-bold"
                          : isDark
                            ? "text-white/80 hover:text-[#FDA400]"
                            : "text-[#1C2B6E]/80 hover:text-[#FDA400]",
                      )}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to="/contact"
                className={cn(
                  "ml-2 xl:ml-4 inline-flex items-center gap-1.5 px-5 py-2 rounded-md font-semibold text-sm whitespace-nowrap transition-all duration-300",
                  isDark
                    ? "bg-white text-[#1C2B6E] hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.15)]"
                    : "bg-[#FDA400] text-[#1C2B6E] hover:bg-[#FDA400]/90 shadow-sm hover:shadow-md",
                )}
              >
                Contact Us <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={cn(
                "lg:hidden p-2 rounded-lg transition-colors",
                isDark
                  ? "text-white hover:bg-white/10"
                  : "text-[#1C2B6E] hover:bg-gray-100",
              )}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Backdrop overlay when dropdown is open */}
        {activeDropdown && (
          <div className="fixed inset-0 top-0 bg-black/10 -z-10 hidden lg:block" />
        )}
      </div>

      {/* ───── Mobile Full-Screen Menu ───── */}
      <div className={cn(
        "fixed inset-0 z-[60] lg:hidden transition-all duration-300",
        isMobileMenuOpen ? "visible" : "invisible pointer-events-none"
      )}>
        {/* Backdrop */}
        <div
          className={cn(
            "absolute inset-0 bg-[#0f1629]/60 backdrop-blur-sm transition-opacity duration-300",
            isMobileMenuOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Slide-in panel */}
        <div className={cn(
          "absolute right-0 top-0 bottom-0 w-full sm:w-[380px] bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col",
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}>
          {/* Mobile header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="/rsa-logo.png" alt="RSA Tech Group" width={100} height={32} className="h-8 w-auto" />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-[#1C2B6E]"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable nav items */}
          <div className="flex-1 overflow-y-auto py-3 px-3">
            {navItems.map((item, itemIdx) => (
              <div key={item.label} className="mobile-item-enter" style={{ animationDelay: `${itemIdx * 40}ms` }}>
                {item.children ? (
                  <div className="mb-1">
                    {/* Accordion trigger */}
                    <button
                      onClick={() =>
                        setOpenMobileSection(openMobileSection === item.label ? null : item.label)
                      }
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 text-left",
                        openMobileSection === item.label
                          ? "bg-[#1C2B6E] text-white"
                          : "text-[#1C2B6E] hover:bg-gray-50",
                      )}
                    >
                      <span className="font-semibold text-[15px]">{item.label}</span>
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform duration-200 flex-shrink-0",
                        openMobileSection === item.label && "rotate-180",
                        openMobileSection === item.label ? "text-[#FDA400]" : "text-gray-400",
                      )} />
                    </button>

                    {/* Accordion content */}
                    <div className={cn(
                      "overflow-hidden transition-all duration-300 ease-out",
                      openMobileSection === item.label ? "max-h-[1000px] opacity-100 mt-1" : "max-h-0 opacity-0",
                    )}>
                      <div className="py-1 pl-2 space-y-0.5">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={cn(
                              "flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200",
                              location.pathname === child.href
                                ? "bg-[#FDA400]/10 text-[#1C2B6E]"
                                : "text-gray-600 hover:bg-gray-50 hover:text-[#1C2B6E]",
                            )}
                          >
                            <div className={cn(
                              "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                              location.pathname === child.href
                                ? "bg-[#1C2B6E] text-[#FDA400]"
                                : "bg-[#1C2B6E]/[0.06] text-[#1C2B6E]/50",
                            )}>
                              <child.icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{child.label}</span>
                                {"badge" in child && child.badge && (
                                  <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FDA400]/15 text-[#FDA400]">
                                    {child.badge}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-[11px] text-gray-400 leading-tight mt-0.5 truncate">{child.desc}</p>
                                {"badge" in child && child.badge === "Flagship" && (
                                  <a
                                    href="https://connexr.com"
                                    target="_blank"
                                    rel="noopener"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex-shrink-0 text-[10px] font-semibold text-[#FDA400] hover:text-[#1C2B6E] transition-colors"
                                  >
                                    connexr.com ↗
                                  </a>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                          </Link>
                        ))}

                        {/* View All link */}
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-2 px-3 py-2.5 text-xs font-bold text-[#FDA400] hover:text-[#1C2B6E] transition-colors"
                        >
                          View all {item.label.toLowerCase()} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center justify-between px-4 py-3.5 rounded-xl transition-colors mb-1",
                      location.pathname === item.href
                        ? "bg-[#FDA400]/10 text-[#1C2B6E] font-bold"
                        : "text-[#1C2B6E] hover:bg-gray-50",
                    )}
                  >
                    <span className="font-semibold text-[15px]">{item.label}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile footer CTA */}
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-1.5 w-full h-12 bg-[#FDA400] text-[#1C2B6E] font-semibold text-sm rounded-md hover:bg-[#FDA400]/90 transition-all"
            >
              Contact Us <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}
